import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, ChevronRight } from "lucide-react";
import TestDayResultModal from "./TestDayResultModal";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PendingTestResultsCard — surfaces outstanding "not recorded yet" test results
 * on the Instructor dashboard. One-tap to log the result from the standard
 * TestDayResultModal (same fair-wording + idempotent logic).
 */
export default function PendingTestResultsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // { learner_id, learner_name }

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/test-results/pending`, { withCredentials: true });
      setItems(data.items || []);
    } catch {
      /* best-effort — dashboard should never error out on this */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || items.length === 0) return null;

  return (
    <div data-testid="pending-test-results-card" className="rounded-2xl bg-indigo-50 dark:bg-indigo-900/15 border border-indigo-200 dark:border-indigo-900/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-indigo-600" />
        <p className="text-xs font-extrabold uppercase tracking-wider text-indigo-800 dark:text-indigo-200">
          Record test results
        </p>
        <span className="ml-auto text-[11px] font-bold text-indigo-700 dark:text-indigo-300">
          {items.length} pending
        </span>
      </div>
      <p className="text-[11px] text-indigo-700 dark:text-indigo-300 mb-2">
        Fair & factual — your entry updates the learner's passport and adds to your verified learner passes supported.
      </p>
      <ul className="space-y-1">
        {items.slice(0, 5).map((it, idx) => (
          <li key={idx}>
            <button
              data-testid={`pending-test-result-${idx}`}
              onClick={async () => {
                // We need a friendly learner name — load it from the first
                // linked student match. Not critical: the modal works without.
                let name = null;
                try {
                  const { data } = await axios.get(
                    `${API}/api/work-mode/students?linked_user_id=${it.learner_id}`,
                    { withCredentials: true },
                  );
                  name = (data?.students || []).find((s) => s.linked_user_id === it.learner_id)?.name;
                } catch { /* best-effort */ }
                setActive({ learner_id: it.learner_id, learner_name: name || "learner" });
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] rounded-xl bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-900/50 hover:border-indigo-400"
            >
              <span className="font-bold text-slate-800 dark:text-slate-100">Test on {it.test_date}</span>
              {it.test_centre && <span className="text-slate-500">· {it.test_centre}</span>}
              <ChevronRight className="w-4 h-4 ml-auto text-indigo-500" />
            </button>
          </li>
        ))}
      </ul>

      <TestDayResultModal
        open={!!active}
        onClose={() => setActive(null)}
        learnerId={active?.learner_id}
        learnerName={active?.learner_name}
        onLogged={() => {
          toast.success("Saved. Thanks for keeping the record fair and factual.");
          setActive(null);
          load();
        }}
      />
    </div>
  );
}
